import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import BackButton from '../../../components/common/BackButton';
import Pagination from '../../../components/common/Pagination';
import {
  getCustomers,
  createCustomer,
  updateCustomer,
  deleteCustomer,
} from '../../../services/customersService';
import { getApiErrorMessage } from '../../../services/http';

const Customers = () => {
  // Create form
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);

  // List & filters
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [onlyActive, setOnlyActive] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Edit state
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const filtered = customers.filter(c => {
    const matchesSearch = !search.trim() ||
      (c.name || '').toLowerCase().includes(search.trim().toLowerCase()) ||
      (c.phone || '').toLowerCase().includes(search.trim().toLowerCase()) ||
      (c.email || '').toLowerCase().includes(search.trim().toLowerCase());
    const matchesActive = onlyActive ? c.isActive !== false : true;
    return matchesSearch && matchesActive;
  });

  const exportToCSV = () => {
    try {
      const cols = [
        { header: 'Name', accessor: r => r.name },
        { header: 'Phone', accessor: r => r.phone },
        { header: 'Email', accessor: r => r.email },
        { header: 'Address', accessor: r => r.address },
        { header: 'Total Jobs', accessor: r => r.totalJobs },
        { header: 'Total Spent', accessor: r => Number(r.totalSpent || 0).toLocaleString('en-KE') },
        { header: 'Last Job Date', accessor: r => r.lastJobDate ? new Date(r.lastJobDate).toLocaleDateString() : '' },
        { header: 'Active', accessor: r => r.isActive ? 'Yes' : 'No' },
      ];
      const header = cols.map(c => '"' + c.header + '"').join(',');
      const body = filtered.map(r => cols.map(c => '"' + (c.accessor(r) ?? '') + '"').join(',')).join('\n');
      const blob = new Blob([header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'customers.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) {
      console.error('Export failed', e);
      toast.error('Export failed');
    }
  };

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const data = await getCustomers({ search: search || undefined, page: currentPage, limit: 10 });
      setCustomers(data?.customers || data || []);
      setTotal(data?.total || 0);
      setTotalPages(data?.totalPages || 1);
    } catch (e) {
      console.error('Load customers failed', e);
      toast.error(getApiErrorMessage(e, 'Failed to load customers'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => loadCustomers(), 400);
    return () => clearTimeout(debounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, currentPage]);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      toast.error('Name and Phone are required');
      return;
    }
    try {
      setCreating(true);
      await createCustomer({
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim() || undefined,
        address: address.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      toast.success('Customer created');
      setName('');
      setPhone('');
      setEmail('');
      setAddress('');
      setNotes('');
      loadCustomers();
    } catch (e) {
      console.error('Create customer failed', e);
      toast.error(getApiErrorMessage(e, 'Failed to create'));
    } finally {
      setCreating(false);
    }
  };

  const onEdit = (c) => {
    setEditId(c._id);
    setEditName(c.name || '');
    setEditPhone(c.phone || '');
    setEditEmail(c.email || '');
    setEditAddress(c.address || '');
    setEditNotes(c.notes || '');
  };

  const onSaveEdit = async () => {
    if (!editId) return;
    if (!editName.trim() || !editPhone.trim()) {
      toast.error('Name and Phone are required');
      return;
    }
    try {
      setSavingEdit(true);
      await updateCustomer(editId, {
        name: editName.trim(),
        phone: editPhone.trim(),
        email: editEmail.trim() || undefined,
        address: editAddress.trim() || undefined,
        notes: editNotes.trim() || undefined,
      });
      toast.success('Customer updated');
      setEditId(null);
      loadCustomers();
    } catch (e) {
      console.error('Update customer failed', e);
      toast.error(getApiErrorMessage(e, 'Failed to update'));
    } finally {
      setSavingEdit(false);
    }
  };

  const onCancelEdit = () => setEditId(null);

  const onDeactivate = async (id) => {
    try {
      await deleteCustomer(id);
      toast.success('Customer deactivated');
      loadCustomers();
    } catch (e) {
      console.error('Deactivate customer failed', e);
      toast.error(getApiErrorMessage(e, 'Failed to deactivate'));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <BackButton to="/dashboard" />
        {/* <h2 className="text-2xl font-bold text-gray-900">Customers</h2> */}
      </div>

      {/* Create Customer */}
      <form onSubmit={onCreate} className="card p-6 space-y-4">
        <h3 className="text-lg font-medium text-gray-900">New Customer</h3>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input className="input-field" value={name} onChange={e => setName(e.target.value)} placeholder="Customer name" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input className="input-field" value={phone} onChange={e => setPhone(e.target.value)} placeholder="07XXXXXXXX" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input type="email" className="input-field" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@example.com" />
          </div>
          <div className="md:col-span-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            <input className="input-field" value={address} onChange={e => setAddress(e.target.value)} placeholder="Street, City" />
          </div>
          <div className="md:col-span-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea className="input-field" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
        </div>
        <button type="submit" className="btn-primary" disabled={creating}>{creating ? 'Saving...' : 'Add Customer'}</button>
      </form>

      {/* Toolbar */}
      <div className="card p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex-1 max-w-md">
            <input className="input-field" placeholder="Search customers" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <label className="ml-4 inline-flex items-center text-sm text-gray-600">
            <input type="checkbox" className="mr-2" checked={onlyActive} onChange={e => setOnlyActive(e.target.checked)} />
            Show active only
          </label>
          <button className="btn-secondary ml-4" onClick={exportToCSV}>Export CSV</button>
        </div>
      </div>

      {/* Customers List */}
      <div className="relative overflow-hidden rounded-2xl shadow-lg border border-emerald-200 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <div className="p-6">
          <h3 className="text-lg font-bold text-emerald-900 mb-4">All Customers</h3>
          <div className="overflow-x-auto bg-white rounded-xl shadow-inner">
            <table className="min-w-full divide-y divide-emerald-100">
              <thead className="bg-gradient-to-r from-emerald-50 to-teal-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold text-emerald-800 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-emerald-800 uppercase tracking-wider">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-emerald-800 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-emerald-800 uppercase tracking-wider">Address</th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-emerald-800 uppercase tracking-wider">Total Jobs</th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-emerald-800 uppercase tracking-wider">Total Spent</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-emerald-800 uppercase tracking-wider">Last Job</th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-emerald-800 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {filtered.map((c, index) => (
                <tr key={c._id} className={`hover:bg-emerald-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {editId === c._id ? (
                      <input className="input-field" value={editName} onChange={e => setEditName(e.target.value)} />
                    ) : (
                      c.name
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {editId === c._id ? (
                      <input className="input-field" value={editPhone} onChange={e => setEditPhone(e.target.value)} />
                    ) : (
                      c.phone
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {editId === c._id ? (
                      <input type="email" className="input-field" value={editEmail} onChange={e => setEditEmail(e.target.value)} />
                    ) : (
                      c.email || '-'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {editId === c._id ? (
                      <input className="input-field" value={editAddress} onChange={e => setEditAddress(e.target.value)} />
                    ) : (
                      c.address || '-'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">{c.totalJobs ?? 0}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">KSh {Number(c.totalSpent || 0).toLocaleString('en-KE')}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{c.lastJobDate ? new Date(c.lastJobDate).toLocaleDateString() : '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                    {editId === c._id ? (
                      <div className="flex justify-end gap-2">
                        <button className="btn-primary" onClick={onSaveEdit} disabled={savingEdit}>{savingEdit ? 'Saving...' : 'Save'}</button>
                        <button className="btn-secondary" onClick={onCancelEdit}>Cancel</button>
                      </div>
                    ) : (
                      <div className="flex justify-end gap-2">
                        <button className="btn-secondary" onClick={() => onEdit(c)}>Edit</button>
                        <button className="btn-danger" onClick={() => onDeactivate(c._id)}>Deactivate</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>
          {loading && <div className="text-center text-gray-500 py-6">Loading...</div>}
          {!loading && filtered.length === 0 && <div className="text-center text-gray-500 py-6">No customers</div>}
        </div>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        total={total}
        pageSize={10}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};

export default Customers; 